<template>
  <div class="ads-page">
    <a-row type="flex" justify="space-between">
      <a-col :lg="8" :md="8" :sm="10" :xs="24">
        <h2 class="list-title">
          <font-awesome-icon
            class="anticon list-icon"
            :icon="['fas', 'bullhorn']"
          />
          <span>{{ labels.title }}</span>
        </h2>
        <p class="list-subtitle">{{ labels.subtitle }}</p>
      </a-col>
      <a-col :lg="16" :md="16" :xs="24">
        <a-row
          type="flex"
          justify="end"
          :gutter="[0, 8]"
          :style="{
            alignItems: 'flex-end',
            marginTop: '25px',
            marginBottom: '10px',
            textAlign: 'right',
          }"
          class="row-reverse"
        >
          <a-col :xs="24" :md="13" :lg="15">
            <a-input-search
              :placeholder="labels.placeholderSearch"
              style="width: 100%"
              @search="handleSearch"
              allowClear
            />
          </a-col>
          <a-col :style="{ textAlign: 'right' }">
            <router-link v-if="canCreate" :to="{ name: 'admin.ads.create' }">
              <a-button type="default" ghost icon="plus" class="add-button">
                <span>{{ labels.add }}</span>
              </a-button>
            </router-link>
          </a-col>
        </a-row>
      </a-col>
    </a-row>
    <a-row>
      <a-col :span="24">
        <StandardTable
          ref="standardTable"
          :fetchService="fetchAds"
          :columns="columns"
          :locale="locale"
          :handleShowTotal="handleShowTotal"
          v-bind="!isMobile && { scroll: { x: true } }"
          :paginationType="isMobile ? 'arrows' : 'numbers'"
          :customRow="handleCustomRow"
          :showSizeChanger="true"
          :pageSizeOptions="['10', '20', '50', '100']"
        />
      </a-col>
    </a-row>
  </div>
</template>

<script>
import _ from "lodash";
import labels from "@/utils/labels";
import StandardTable from "../../../components/core/table/StandardTable.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import {
  DATE_FORMAT,
  adminPageTitle,
  hasRoles,
  roles,
} from "../../../utils/utils";
export default {
  components: { StandardTable },
  name: "AdList",
  metaInfo: {
    title: adminPageTitle("Anuncios"),
  },
  filters: {
    ucwords(value) {
      if (!value) return "";
      value = value.toString();
      return value
        .split(" ")
        .map((item) => _.capitalize(item))
        .join(" ");
    },
  },
  data() {
    return {
      labels: labels.ads,
      search: "",
      activeRow: null,
    };
  },
  methods: {
    ...mapActions("ads", ["fetchAds", "deleteAd"]),
    handleReloadTable() {
      let filtersTable = this.$refs.standardTable.filters;
      let result = { ...filtersTable };
      this.$refs.standardTable.handleFilters(result);
    },
    handleSearch(search) {
      this.$refs.standardTable.handleSearch(search);
    },
    handleShowTotal(total, range) {
      return `${range[0]}-${range[1]} de ${total}`;
    },
    confirmDelete(id) {
      this.$confirm({
        title: this.labels.confirm.title,
        content: this.labels.confirm.subtitle,
        okText: this.labels.confirm.confirmButtonText,
        okType: "danger",
        icon: "close-circle",
        cancelText: this.labels.confirm.cancelButtonText,
        onOk: async () => {
          try {
            await this.deleteAd(id);
            this.$message.success(this.labels.deletedSuccess);
            this.handleReloadTable();
          } catch (error) {
            this.$message.error(
              error?.response?.data?.message ||
                error?.response?.message ||
                error.message ||
                this.labels.deletedError
            );
          }
        },
      });
    },
    handleCustomRow(_, index) {
      return {
        on: {
          click: () => {
            this.activeRow = this.activeRow === index ? null : index;
          },
        },
      };
    },
  },
  computed: {
    ...mapGetters("adminAuth", ["getLoggedUser"]),
    ...mapState("layout", ["screenWidth"]),
    isMobile() {
      return this.screenWidth < 600;
    },
    locale() {
      return {
        emptyText: this.labels.table.emptyText,
      };
    },
    canCreate() {
      const { accessRoles } = this.getLoggedUser;
      return hasRoles(accessRoles, [roles.admin.ROLE_ADS_CREATE]);
    },
    canDelete() {
      const { accessRoles } = this.getLoggedUser;
      return hasRoles(accessRoles, [roles.admin.ROLE_ADS_DELETE]);
    },
    columns() {
      return [
        {
          title: this.labels.table.columns.id,
          dataIndex: "id",
          key: "id",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text) => {
            return `${text?.split("-")?.shift()}`;
          },
        },
        {
          title: this.labels.table.columns.title,
          dataIndex: "title",
          key: "title",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text) => {
            const shortText =
              text.length > 20 ? text.slice(0, 20) + "..." : text;
            return <span title={text}>{shortText}</span>;
          },
        },
        {
          title: this.labels.table.columns.type,
          dataIndex: "type",
          key: "type",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text) => {
            return text ? this.labels.table.columns.type[text] : "N/A";
          },
        },

        {
          title: this.labels.table.columns.startDate,
          dataIndex: "start_date",
          key: "start_date",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text) => {
            return this.$moment(text).format("DD/MM/YYYY");
          },
        },
        {
          title: this.labels.table.columns.endDate,
          dataIndex: "end_date",
          key: "end_date",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text) => {
            return this.$moment(text).format("DD/MM/YYYY");
          },
        },
        {
          title: this.labels.table.columns.active,
          dataIndex: "active",
          key: "active",
          sorter: true,
          hidden: this.isMobile,
          customRender: (text) => {
            return text
              ? this.labels.table.columns.active.yes
              : this.labels.table.columns.active.no;
          },
        },
        {
          title: this.labels.table.columns.action.title,
          dataIndex: "action",
          key: "action",
          width: "120px",
          hidden: this.isMobile,
          customRender: (_, record) => {
            return (
              <div class="action-buttons">
                <router-link
                  to={{
                    name: "admin.ads.edit",
                    params: { id: record.id },
                  }}
                  title={this.labels.table.columns.action.edit}
                >
                  <a-button
                    type="link"
                    icon="edit"
                    class="action-button-edit"
                  />
                </router-link>
                {this.canDelete && (
                  <a-button
                    type="link"
                    icon="close-circle"
                    class="action-button-delete"
                    onClick={() => this.confirmDelete(record.id)}
                    title={this.labels.table.columns.action.delete}
                  />
                )}
              </div>
            );
          },
        },
        {
          title: this.labels.table.columns.ads,
          dataIndex: "ads",
          key: "ads",
          sorter: false,
          hidden: !this.isMobile,
          customRender: (_text, record, index) => {
            // Card item design
            const color = record.active ? "#28a745" : "#f50";
            return (
              <div class="list-item">
                <div class="list-item-head">
                  <div
                    class="list-item-head-header"
                    style={{ borderColor: color }}
                  >
                    <span>
                      {this.$options.filters.ucwords(
                        this.$moment(record.start_date).format(
                          DATE_FORMAT.MOMENT_DAY_MONTH_YEAR
                        )
                      )}
                    </span>
                    <small>
                      {this.$moment(record.start_date)
                        .format(DATE_FORMAT.MOMENT_TIME)
                        .toUpperCase()}
                    </small>
                  </div>
                  <div class="list-item-head-content">
                    <div class="list-item-head-content-title">
                      <span>{record.title}</span>
                    </div>
                    <div class="list-item-head-content-type">
                      <span>{this.labels.table.columns.type[record.type]}</span>
                    </div>
                  </div>
                  <div class="list-item-head-footer">
                    <div class="list-item-head-footer-width">
                      <span>
                        W({record.width})/H({record.height})
                      </span>
                    </div>
                    <div class="list-item-head-footer-status">
                      <a-tag color={color}>
                        {record.active
                          ? this.labels.table.columns.active.yes
                          : this.labels.table.columns.active.no}
                      </a-tag>
                    </div>
                  </div>
                </div>
                {this.activeRow === index && (
                  <div class="list-item-actions">
                    <a-button-group>
                      <router-link
                        class="edit-button ant-btn ant-btn-primary ant-btn-ad"
                        to={{
                          name: "admin.ads.edit",
                          params: { id: record.id },
                        }}
                        title={this.labels.table.columns.action.edit}
                      >
                        {this.labels.table.columns.action.edit}
                      </router-link>
                      {this.canDelete && (
                        <a-button
                          class="delete-button ant-btn ant-btn-danger"
                          onClick={() => this.confirmDelete(record.id)}
                          title={this.labels.table.columns.action.delete}
                          type="danger"
                        >
                          {this.labels.table.columns.action.delete}
                        </a-button>
                      )}
                    </a-button-group>
                  </div>
                )}
              </div>
            );
          },
        },
      ].filter((column) => !column.hidden);
    },
    serverDateFormat() {
      return "YYYY-MM-DD HH:mm:ss";
    },
  },
};
</script>
<style lang="scss">
@import "../../../assets/scss/variable";
.ads-page {
  .list {
    &-title {
      margin-bottom: 10px;
      .list-icon {
        margin-right: 10px;
      }
    }
    &-subtitle {
      margin-bottom: 10px;
    }
  }
  .add-button {
    color: color(primary);
    border-color: color(primary);
    margin-left: 10px;
  }
  .action-buttons {
    display: flex;
    justify-content: space-between;
    .action-button-edit {
      color: color(primary);
    }
    .action-button-delete {
      color: color(danger);
    }
  }
  @media screen and (max-width: $screen-mobile) {
    .row-reverse {
      flex-direction: column-reverse;
    }
  }
  .list-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    &-head {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: 6px;
      &-header {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 2px;
        max-width: 25%;
        width: 25%;
        border-right: 4px solid color(border);
        .success {
          color: color(success);
        }
        .warning {
          color: color(warning);
        }
        .danger {
          color: color(danger);
        }
        span {
          font-size: 14px;
          font-weight: 600;
        }
        small {
          font-size: 12px;
          font-weight: 400;
          padding-right: 4px;
        }
      }
      &-content {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        gap: 2px;
        max-width: 50%;
        width: 50%;
        overflow: hidden;
        &-plays {
          span {
            font-size: 16px;
            font-weight: 600;
            text-overflow: ellipsis;
          }
        }
        &-ad-type {
          span {
            font-size: 14px;
            font-weight: 500;
            text-overflow: ellipsis;
          }
        }
        &-lotteries {
          span {
            font-size: 14px;
            font-weight: 500;
            text-overflow: ellipsis;
          }
        }
      }
      &-footer {
        max-width: 25%;
        width: 25%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: right;
        gap: 2px;
        &-amount {
          span {
            font-size: 14px;
            font-weight: 600;
          }
        }
        &-status {
          width: 100%;
          span {
            font-size: 12px;
            font-weight: 400;
          }
          .ant-tag {
            padding: 0;
            margin: 0;
            padding-left: 2px;
            padding-right: 2px;
          }
        }
      }
    }
    &-actions {
      width: 100%;
      margin-top: 10px;
      .ant-btn-group {
        width: 100%;
        .more-info-button,
        .edit-button,
        .delete-button {
          width: 100%;
        }
      }
    }
  }
}
.w-100 {
  display: ad;
  width: 100%;
  margin-bottom: 5px;
}
</style>
